import { CancelRounded, CheckCircleRounded, ErrorRounded } from '@mui/icons-material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, CircularProgress, Tab, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import API from 'api/API';
import { UploadType } from 'api/Responses/BulkUploadResponses';
import { useLanguage } from 'context/AppContext';
import { store } from 'store';
import { selectDataUpload, updateData } from 'store/dataUpload/dataUploadSlice';
import { DataUploadCheckStatus } from '../DataUpload.helpers';
import DataUploadMergeTable from './DataUploadMergeTable';
import * as Styles from './EditDataUploadMerge.styles';

interface EditDataUploadMergeParams {
  uploadId: string;
  uploadType: UploadType;
}

const EditDataUploadMerge: FC<EditDataUploadMergeParams> = ({ uploadId, uploadType }) => {
  const [value, setValue] = useState<DataUploadCheckStatus>(DataUploadCheckStatus.UNIQUE);
  const [language] = useLanguage();

  const dataUpload = useSelector(selectDataUpload);
  const [isLoading, setIsLoading] = useState(true);
  const handleChangeTab = (event: React.SyntheticEvent, newValue: DataUploadCheckStatus) => {
    setValue(newValue);
  };

  // load initial check results state into slice state
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const response = await API.bulkUpload.getUploadChecksResults(uploadId);
        store.dispatch(updateData(response));
      } catch (error) {
        API.errorHandler(error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [uploadId]);

  if (isLoading) {
    return (
      <Styles.LoaderContainer>
        <CircularProgress />
      </Styles.LoaderContainer>
    );
  }

  return (
    <Styles.DataUploadMergeContent>
      <Styles.Info>
        <Typography variant="h5">
          {language.DataUpload.editDataUploadMerge.filename}
          <b>{dataUpload.fileName}</b>
        </Typography>
        <Typography variant="subtitle1">{language.DataUpload.editDataUploadMerge.inspectInstruction}</Typography>
      </Styles.Info>
      <TabContext value={value}>
        <Box style={{ padding: '20px' }}>
          <TabList onChange={handleChangeTab}>
            <Tab
              label={
                <Styles.TabLabel>
                  <CheckCircleRounded />
                  {DataUploadCheckStatus.UNIQUE}: {dataUpload.checkState.unique.length}
                </Styles.TabLabel>
              }
              value={DataUploadCheckStatus.UNIQUE}
            />
            <Tab
              label={
                <Styles.TabLabel>
                  <ErrorRounded />
                  {DataUploadCheckStatus.UPDATE}: {dataUpload.checkState.update.length}
                </Styles.TabLabel>
              }
              value={DataUploadCheckStatus.UPDATE}
            />
            <Tab
              label={
                <Styles.TabLabel>
                  <CancelRounded />
                  {DataUploadCheckStatus.IGNORE}: {dataUpload.checkState.ignore.length}
                </Styles.TabLabel>
              }
              value={DataUploadCheckStatus.IGNORE}
            />
          </TabList>
        </Box>
        <TabPanel value={DataUploadCheckStatus.UNIQUE}>
          <DataUploadMergeTable
            uploadType={uploadType}
            checkStatus={DataUploadCheckStatus.UNIQUE}
            uploadId={uploadId}
            isLoading={isLoading}
          />
        </TabPanel>
        <TabPanel value={DataUploadCheckStatus.UPDATE}>
          <DataUploadMergeTable
            uploadType={uploadType}
            checkStatus={DataUploadCheckStatus.UPDATE}
            uploadId={uploadId}
            isLoading={isLoading}
          />
        </TabPanel>
        <TabPanel value={DataUploadCheckStatus.IGNORE}>
          <DataUploadMergeTable
            uploadType={uploadType}
            checkStatus={DataUploadCheckStatus.IGNORE}
            uploadId={uploadId}
            isLoading={isLoading}
          />
        </TabPanel>
      </TabContext>
    </Styles.DataUploadMergeContent>
  );
};

export default EditDataUploadMerge;
